/** Minimal GLB (glTF 2.0 binary) reader: node hierarchy, TRS, extras, triangle meshes. */

export interface GltfNode {
  name: string;
  world: Float64Array; // column-major 4x4
  meshIndex?: number;
  extras: Record<string, unknown>;
}

export interface GltfMeshPrim {
  positions: Float32Array;
  indices: Uint32Array;
  material?: number;
}

export interface GltfDoc {
  nodes: GltfNode[];
  /** A mesh's triangle primitives, decoded on first use (render-only meshes are never read). */
  mesh(index: number): GltfMeshPrim[];
}

interface RawNode {
  name?: string;
  mesh?: number;
  children?: number[];
  translation?: number[];
  rotation?: number[];
  scale?: number[];
  matrix?: number[];
  extras?: Record<string, unknown>;
}

const COMPONENT_SIZE: Record<number, number> = { 5120: 1, 5121: 1, 5122: 2, 5123: 2, 5125: 4, 5126: 4 };
const TYPE_COUNT: Record<string, number> = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT4: 16 };

export function parseGlb(data: ArrayBuffer | Uint8Array): GltfDoc {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(0, true) !== 0x46546c67) throw new Error("not a GLB file");
  let offset = 12;
  let json: any;
  let bin: Uint8Array | undefined;
  while (offset < bytes.byteLength) {
    const len = view.getUint32(offset, true);
    const type = view.getUint32(offset + 4, true);
    const chunk = bytes.subarray(offset + 8, offset + 8 + len);
    if (type === 0x4e4f534a) json = JSON.parse(new TextDecoder().decode(chunk));
    else if (type === 0x004e4942) bin = chunk;
    offset += 8 + len;
  }
  if (!json) throw new Error("GLB missing JSON chunk");

  const readAccessor = (idx: number): Float32Array | Uint32Array => {
    const acc = json.accessors[idx];
    const bv = json.bufferViews[acc.bufferView];
    const comps = TYPE_COUNT[acc.type]!;
    const csize = COMPONENT_SIZE[acc.componentType]!;
    const stride = bv.byteStride ?? comps * csize;
    const base = (bv.byteOffset ?? 0) + (acc.byteOffset ?? 0);
    const dv = new DataView(bin!.buffer, bin!.byteOffset, bin!.byteLength);
    const out = acc.componentType === 5126 ? new Float32Array(acc.count * comps) : new Uint32Array(acc.count * comps);
    for (let i = 0; i < acc.count; i++) {
      for (let c = 0; c < comps; c++) {
        const o = base + i * stride + c * csize;
        let v: number;
        switch (acc.componentType) {
          case 5126:
            v = dv.getFloat32(o, true);
            break;
          case 5125:
            v = dv.getUint32(o, true);
            break;
          case 5123:
            v = dv.getUint16(o, true);
            break;
          default:
            v = dv.getUint8(o);
        }
        out[i * comps + c] = v;
      }
    }
    return out;
  };

  const decoded = new Map<number, GltfMeshPrim[]>();
  const mesh = (index: number): GltfMeshPrim[] => {
    let prims = decoded.get(index);
    if (!prims) {
      prims = (json.meshes?.[index]?.primitives ?? [])
        .filter((p: any) => p.mode === undefined || p.mode === 4)
        .map((p: any) => {
          const positions = readAccessor(p.attributes.POSITION) as Float32Array;
          const indices =
            p.indices !== undefined
              ? Uint32Array.from(readAccessor(p.indices))
              : Uint32Array.from({ length: positions.length / 3 }, (_, i) => i);
          return { positions, indices, material: p.material };
        }) as GltfMeshPrim[];
      decoded.set(index, prims);
    }
    return prims;
  };

  const rawNodes: RawNode[] = json.nodes ?? [];
  const nodes: GltfNode[] = [];
  const visit = (idx: number, parent: Float64Array) => {
    const n = rawNodes[idx]!;
    const world = mul(parent, localMatrix(n));
    nodes.push({ name: n.name ?? `node${idx}`, world, meshIndex: n.mesh, extras: n.extras ?? {} });
    for (const c of n.children ?? []) visit(c, world);
  };
  const scene = json.scenes?.[json.scene ?? 0];
  const roots: number[] = scene?.nodes ?? rawNodes.map((_, i) => i);
  for (const r of roots) visit(r, identity());
  return { nodes, mesh };
}

export function identity(): Float64Array {
  const m = new Float64Array(16);
  m[0] = m[5] = m[10] = m[15] = 1;
  return m;
}

function localMatrix(n: RawNode): Float64Array {
  if (n.matrix) return Float64Array.from(n.matrix);
  const [tx, ty, tz] = n.translation ?? [0, 0, 0];
  const [qx, qy, qz, qw] = n.rotation ?? [0, 0, 0, 1];
  const [sx, sy, sz] = n.scale ?? [1, 1, 1];
  const m = new Float64Array(16);
  const xx = qx! * qx!, yy = qy! * qy!, zz = qz! * qz!;
  const xy = qx! * qy!, xz = qx! * qz!, yz = qy! * qz!;
  const wx = qw! * qx!, wy = qw! * qy!, wz = qw! * qz!;
  m[0] = (1 - 2 * (yy + zz)) * sx!;
  m[1] = 2 * (xy + wz) * sx!;
  m[2] = 2 * (xz - wy) * sx!;
  m[4] = 2 * (xy - wz) * sy!;
  m[5] = (1 - 2 * (xx + zz)) * sy!;
  m[6] = 2 * (yz + wx) * sy!;
  m[8] = 2 * (xz + wy) * sz!;
  m[9] = 2 * (yz - wx) * sz!;
  m[10] = (1 - 2 * (xx + yy)) * sz!;
  m[12] = tx!;
  m[13] = ty!;
  m[14] = tz!;
  m[15] = 1;
  return m;
}

function mul(a: Float64Array, b: Float64Array): Float64Array {
  const o = new Float64Array(16);
  for (let c = 0; c < 4; c++)
    for (let r = 0; r < 4; r++) {
      let s = 0;
      for (let k = 0; k < 4; k++) s += a[k * 4 + r]! * b[c * 4 + k]!;
      o[c * 4 + r] = s;
    }
  return o;
}

export function transformPoint(m: Float64Array, x: number, y: number, z: number): [number, number, number] {
  return [
    m[0]! * x + m[4]! * y + m[8]! * z + m[12]!,
    m[1]! * x + m[5]! * y + m[9]! * z + m[13]!,
    m[2]! * x + m[6]! * y + m[10]! * z + m[14]!,
  ];
}
